import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Topbar } from '@/components/Topbar';
import { Button } from '@/components/Button';
import { Card, SectionHeader } from '@/components/Card';
import {
  facebookLeadFormsService,
  type LeadGenFormDetail,
  type LeadRow,
} from '@/services/facebook-lead-forms.service';
import { ArrowLeft, Archive, Download, Inbox, ClipboardList } from 'lucide-react';
import toast from 'react-hot-toast';
import { formatDistanceToNow } from 'date-fns';

function relativeTime(ts?: string) {
  if (!ts) return '—';
  try { return formatDistanceToNow(new Date(ts), { addSuffix: true }); } catch { return ts; }
}

export default function LeadFormDetail() {
  const { formId } = useParams<{ formId: string }>();
  const nav = useNavigate();
  const [form, setForm] = useState<LeadGenFormDetail | null>(null);
  const [leads, setLeads] = useState<LeadRow[]>([]);
  const [nextCursor, setNextCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [archiving, setArchiving] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    if (!formId) return;
    setLoading(true);
    Promise.all([
      facebookLeadFormsService.getById(formId),
      facebookLeadFormsService.fetchLeads(formId, { limit: 50 }).catch(() => ({ data: [] as LeadRow[], next: undefined })),
    ])
      .then(([f, l]) => { setForm(f); setLeads(l.data); setNextCursor(l.next); })
      .catch((e: any) => {
        if (!e?.response) toast.error(e?.message ?? 'Failed to load form');
      })
      .finally(() => setLoading(false));
  }, [formId]);

  const loadMoreLeads = async () => {
    if (!formId || !nextCursor) return;
    setLoadingMore(true);
    try {
      const r = await facebookLeadFormsService.fetchLeads(formId, { limit: 50, after: nextCursor });
      setLeads(prev => [...prev, ...r.data]);
      setNextCursor(r.next);
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Failed to load more leads');
    } finally { setLoadingMore(false); }
  };

  const archive = async () => {
    if (!form || form.status === 'ARCHIVED') return;
    if (!window.confirm(`Archive "${form.name}"?\n\nMeta doesn't allow un-archiving via API. Existing leads stay accessible, but the form stops accepting new submissions.`)) return;
    setArchiving(true);
    try {
      await facebookLeadFormsService.remove(form.id);
      toast.success('Form archived');
      setForm({ ...form, status: 'ARCHIVED' });
    } catch (e: any) {
      if (!e?.response) toast.error(e?.message ?? 'Archive failed');
    } finally {
      setArchiving(false);
    }
  };

  const exportCsv = () => {
    if (leads.length === 0) { toast.error('No leads to export'); return; }
    // Build CSV: id, created_time, ad_id, ad_name, then one column per unique question key
    const keys = new Set<string>();
    for (const l of leads) for (const f of l.field_data) keys.add(f.name);
    const keyList = Array.from(keys);
    const header = ['id', 'created_time', 'ad_id', 'ad_name', 'adset_id', 'campaign_id', ...keyList];
    const rows = leads.map(l => {
      const fieldMap = new Map<string, string>();
      for (const f of l.field_data) fieldMap.set(f.name, (f.values ?? []).join(' | '));
      return [
        l.id, l.created_time ?? '', l.ad_id ?? '', l.ad_name ?? '',
        l.adset_id ?? '', l.campaign_id ?? '',
        ...keyList.map(k => fieldMap.get(k) ?? ''),
      ];
    });
    const csv = [header, ...rows].map(r =>
      r.map(cell => {
        const s = String(cell ?? '');
        return /["\n,]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      }).join(',')
    ).join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${form?.name ?? 'leads'}_${formId}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading || !form) {
    return (
      <>
        <Topbar title="Lead Form" breadcrumb="Facebook Ads / Lead Forms" />
        <div className="px-7 py-7 space-y-3 max-w-[1000px] mx-auto">
          <div className="skeleton h-16 rounded-2xl" />
          <div className="skeleton h-48 rounded-2xl" />
        </div>
      </>
    );
  }

  return (
    <>
      <Topbar
        title={form.name}
        breadcrumb={`Facebook Ads / Lead Forms / ${form.name}`}
        right={
          <div className="flex gap-2">
            <Button variant="ghost" iconLeft={<ArrowLeft size={14} />} onClick={() => nav('/facebook/lead-forms')}>
              Back
            </Button>
            <Button variant="secondary" iconLeft={<Download size={14} />} onClick={exportCsv} disabled={leads.length === 0}>
              Export CSV
            </Button>
            {form.status === 'ACTIVE' && (
              <Button variant="secondary" iconLeft={<Archive size={14} />} loading={archiving} onClick={archive}>
                Archive
              </Button>
            )}
          </div>
        }
      />

      <div className="mx-auto w-full max-w-[1000px] px-7 py-7 space-y-5">
        {/* Status + meta */}
        <Card>
          <div className="grid grid-cols-2 gap-3 p-1 text-sm md:grid-cols-4">
            <Stat label="Status" value={
              <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-semibold ${
                form.status === 'ACTIVE' ? 'bg-success-soft text-success' : 'bg-surface-alt text-text-muted'
              }`}>{form.status ?? 'UNKNOWN'}</span>
            } />
            <Stat label="Leads" value={<strong className="tabular-nums">{form.leads_count ?? 0}</strong>} />
            <Stat label="Locale" value={<span className="font-mono">{form.locale ?? '—'}</span>} />
            <Stat label="Created" value={relativeTime(form.created_time)} />
          </div>
        </Card>

        {/* Questions schema */}
        <Card>
          <SectionHeader title="Questions" icon={<ClipboardList size={16} />} subtitle={`${form.questions?.length ?? 0} fields`} />
          {!form.questions?.length ? (
            <p className="text-xs italic text-text-muted">No question metadata returned by Meta.</p>
          ) : (
            <div className="space-y-1.5">
              {form.questions.map((q, i) => (
                <div key={i} className="flex items-center gap-2 rounded-lg border border-border bg-surface p-2.5 text-sm">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-soft text-2xs font-bold text-primary">{i + 1}</span>
                  <div className="flex-1 min-w-0">
                    <p className="truncate font-semibold text-text-primary">{String(q.label ?? q.type ?? 'Untitled')}</p>
                    <p className="font-mono text-2xs text-text-muted">{String(q.type ?? '?')}{q.key ? ` · key=${q.key}` : ''}</p>
                  </div>
                  {Array.isArray(q.options) && q.options.length > 0 && (
                    <span className="rounded-full bg-surface-alt px-2 py-0.5 text-2xs text-text-muted">{q.options.length} options</span>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>

        {/* Leads inbox */}
        <Card>
          <SectionHeader
            title="Submitted leads"
            icon={<Inbox size={16} />}
            subtitle={leads.length === 0 ? 'No leads yet' : `${leads.length} most recent`}
          />
          {leads.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-surface-alt py-10 text-center">
              <Inbox size={28} className="mx-auto mb-2 text-text-muted opacity-40" />
              <p className="text-sm font-semibold text-text-primary">No submissions yet</p>
              <p className="mx-auto mt-1 max-w-sm text-xs text-text-muted">
                Once your ads start running and people submit this form, their answers show up here in real time.
              </p>
            </div>
          ) : (
            <div className="-mx-4 -mb-4 overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border bg-surface-alt">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-text-muted">Submitted</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-text-muted">Lead</th>
                    <th className="px-4 py-2 text-left text-xs font-semibold text-text-muted">Ad</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-border">
                  {leads.map((l) => (
                    <tr key={l.id} className="hover:bg-surface-alt">
                      <td className="px-4 py-2 text-xs text-text-muted whitespace-nowrap">{relativeTime(l.created_time)}</td>
                      <td className="px-4 py-2">
                        <div className="space-y-0.5">
                          {l.field_data.slice(0, 3).map((f, i) => (
                            <div key={i} className="text-xs">
                              <span className="text-text-muted">{f.name}: </span>
                              <span className="font-medium text-text-primary">{(f.values ?? []).join(', ')}</span>
                            </div>
                          ))}
                          {l.field_data.length > 3 && (
                            <p className="text-2xs italic text-text-muted">+{l.field_data.length - 3} more fields</p>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-2 text-xs">
                        {l.ad_name ? (
                          <>
                            <p className="font-medium text-text-primary truncate max-w-[200px]">{l.ad_name}</p>
                            <p className="font-mono text-2xs text-text-muted truncate max-w-[200px]">{l.ad_id}</p>
                          </>
                        ) : (
                          <span className="text-text-muted">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {nextCursor && (
                <div className="border-t border-border px-4 py-3 flex justify-center">
                  <Button variant="secondary" size="sm" loading={loadingMore} onClick={loadMoreLeads}>
                    Load more leads
                  </Button>
                </div>
              )}
            </div>
          )}
        </Card>
      </div>
    </>
  );
}

const Stat: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div>
    <p className="text-2xs font-semibold uppercase tracking-wider text-text-muted">{label}</p>
    <p className="mt-0.5 text-sm font-medium text-text-primary">{value}</p>
  </div>
);
